import { Grid, SelectChangeEvent } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { useLocalStorage } from 'usehooks-ts';

import { Select } from '@/components/Form/Select';
import { IconName } from '@/components/Icon';

import { COUNTRY_FLAGS } from '@/constants/countryCodes';
import { ROUTE } from '@/constants/routes';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectTerritories } from '@/redux/countries';

import { Option } from '@/types/components/common';
import { CountryISO } from '@/types/places';

import { getCookie, getDataTestId } from '@/utils/browser';
import { navigateToAppDomain } from '@/utils/domain';
import {
  getTenantForCountryCode,
  saveCountryCodeAndSwitchTenant,
} from '@/utils/languages';

import {
  COMING_SOON_COUNTRIES,
  getModifiedCountryName,
  HIDDEN_COUNTRIES,
} from '../../constant/countries';

type CountrySelectProps = {
  disabled: boolean;
};

export const LS_COUNTRY_KEY = 'onboarding_selected_country';

export const CountrySelect = ({ disabled }: CountrySelectProps) => {
  const { t, switchTenant, language } = useTranslate();
  const navigate = useNavigate();
  const { tenant: tenantParam } = useParams();
  const { territories, territoryByCountryCode } =
    useAppSelector(selectTerritories);
  const { control, formState, setValue, watch, reset } = useFormContext();
  const countryCode = watch('countryCode');
  const [, setSavedCountry] = useLocalStorage(LS_COUNTRY_KEY, '');
  const currentCountryCode = useRef<string | null>(null);

  const selectedTenant = useMemo(
    () => getTenantForCountryCode(territoryByCountryCode, countryCode),
    [countryCode, territoryByCountryCode],
  );

  const countryOptions: Option[] = useMemo(() => {
    const testBeta = getCookie('test-beta');
    const isTestMode = testBeta === '1';

    const comingSoonCountries: string[] = isTestMode
      ? []
      : [...COMING_SOON_COUNTRIES];

    const hiddenCountries: string[] = isTestMode ? [] : [...HIDDEN_COUNTRIES];

    const filteredTerritories = territories.filter(
      ({ countryCode }) => !hiddenCountries.includes(countryCode),
    );

    const countryOptions: Option[] = filteredTerritories.map(
      ({ countryCode, country, tenant }) => {
        const isComingSoon = comingSoonCountries.includes(countryCode);

        return {
          value: countryCode,
          text: isComingSoon
            ? `${getModifiedCountryName(country)} (${t('countrySelect.form.country.comingSoon')})`
            : getModifiedCountryName(country),
          icon: COUNTRY_FLAGS[tenant] as IconName,
          disabled: isComingSoon,
        };
      },
    );

    return countryOptions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [territories, language]);

  const lastRouteTenantSynced = useRef<string | null>(null);
  const hasInitialTenantSynced = useRef(false);

  useEffect(() => {
    if (hasInitialTenantSynced.current || !countryCode) {
      return;
    }
    const tenant = getTenantForCountryCode(territoryByCountryCode, countryCode);
    if (!tenant) {
      return;
    }
    hasInitialTenantSynced.current = true;
    void switchTenant(tenant);
  }, [countryCode, territoryByCountryCode, switchTenant]);

  const handleAfterCountryFieldChanged = (e: SelectChangeEvent<unknown>) => {
    const nextCountryCode = e.target.value as string;
    if (nextCountryCode === CountryISO.Cayman) {
      reset({ countryCode: currentCountryCode.current });
      navigateToAppDomain(nextCountryCode);
      return;
    }

    const resolvedTenant = saveCountryCodeAndSwitchTenant(
      nextCountryCode,
      territoryByCountryCode,
      setSavedCountry,
      switchTenant,
    );
    currentCountryCode.current = nextCountryCode;
    if (!resolvedTenant) {
      return;
    }
    if (resolvedTenant !== selectedTenant) {
      if (!navigateToAppDomain(nextCountryCode)) {
        navigate(`${ROUTE.ONBOARDING}/${resolvedTenant}`, {
          replace: true,
        });
      }
    }
  };

  useEffect(() => {
    if (!countryOptions?.length) {
      return;
    }

    if (!tenantParam) {
      lastRouteTenantSynced.current = null;
      return;
    }

    if (lastRouteTenantSynced.current === tenantParam) {
      return;
    }

    const found = countryOptions.find(
      option => option.value === tenantParam.toUpperCase() && !option.disabled,
    );
    if (!found?.value) {
      return;
    }

    lastRouteTenantSynced.current = tenantParam;
    const code = String(found.value);
    setValue('countryCode', code);
    saveCountryCodeAndSwitchTenant(
      code,
      territoryByCountryCode,
      setSavedCountry,
      switchTenant,
    );
  }, [
    countryOptions,
    tenantParam,
    territoryByCountryCode,
    setValue,
    setSavedCountry,
    switchTenant,
  ]);

  return (
    <Grid item xs={12}>
      <Select
        options={countryOptions}
        control={control}
        name="countryCode"
        label={t('countrySelect.form.country.label')}
        error={!!formState.errors.countryCode}
        helperText={formState.errors.countryCode?.message as string}
        rules={{
          required: t('countrySelect.form.country.error.required'),
        }}
        fullWidth
        disabled={disabled}
        inputProps={{
          ...getDataTestId('countrycode-select-input'),
        }}
        onSelectChange={handleAfterCountryFieldChanged}
      />
    </Grid>
  );
};
